import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, API_BASE, tokenStore } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  MapPin,
  Users2,
  HardHat,
  ClipboardCheck,
  ChevronRight,
  Download,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  CalendarDays,
  Footprints,
} from "lucide-react";

function istTodayKey() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtDdMmYyyy(yyyymmdd) {
  if (!yyyymmdd || typeof yyyymmdd !== "string") return "";
  const [y, m, d] = yyyymmdd.split("-");
  return `${d}.${m}.${y}`;
}

/* ------------------------------------------------------------- Tile shell */
function Tile({ accent, icon: Icon, title, subtitle, value, valueAccent, onClick, testid, children }) {
  // accent: 'indigo' | 'emerald' | 'amber' | 'sky'
  const accents = {
    indigo: { bar: "bg-indigo-600", iconBg: "bg-indigo-100", iconFg: "text-indigo-700", value: "text-indigo-700" },
    emerald: { bar: "bg-emerald-600", iconBg: "bg-emerald-100", iconFg: "text-emerald-700", value: "text-emerald-700" },
    amber: { bar: "bg-amber-500", iconBg: "bg-amber-100", iconFg: "text-amber-700", value: "text-amber-700" },
    sky: { bar: "bg-sky-600", iconBg: "bg-sky-100", iconFg: "text-sky-700", value: "text-sky-700" },
    slate: { bar: "bg-slate-600", iconBg: "bg-slate-100", iconFg: "text-slate-700", value: "text-slate-800" },
  };
  const a = accents[accent] || accents.slate;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group relative flex w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-left transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400"
    >
      <span className={`w-1.5 shrink-0 ${a.bar}`} aria-hidden />
      <div className="flex-1 p-4">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${a.iconBg}`}>
            <Icon className={`h-5 w-5 ${a.iconFg}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{title}</div>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            {subtitle && (
              <div className="text-[11px] font-medium text-amber-700/90 mt-0.5 truncate">{subtitle}</div>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-baseline gap-1">
          <div className={`text-3xl font-semibold tabular-nums ${valueAccent || a.value}`}>{value}</div>
        </div>
        {children}
      </div>
    </button>
  );
}

/* -------------------------------------------------- generic drill-down */
function DrillDownDialog({
  open, onOpenChange, title, subtitle, columns, rows, loading,
  onDownload, downloadBusy, extraHeader,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]">{title}</DialogTitle>
          {subtitle && <p className="text-[12px] text-slate-500 -mt-1">{subtitle}</p>}
        </DialogHeader>
        {extraHeader && <div className="border-b pb-3">{extraHeader}</div>}
        <div className="overflow-auto rounded border" style={{ minHeight: "30vh" }}>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading&hellip;
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">No records to display.</div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-slate-500">
                  {columns.map((c) => (
                    <th key={c.key} className={`py-2 px-3 ${c.align === "right" ? "text-right" : ""}`}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r._key || i} className="border-b last:border-b-0 hover:bg-slate-50/60">
                    {columns.map((c) => (
                      <td key={c.key} className={`py-1.5 px-3 ${c.align === "right" ? "text-right tabular-nums" : ""}`}>
                        {c.render ? c.render(r, i) : (r[c.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {(() => {
                // Render a totals row summing numeric values across all rows
                // for right-aligned (numeric) columns only.
                const numericKeys = columns
                  .filter((c) => c.align === "right" || c.numeric)
                  .map((c) => c.key);
                const totals = {};
                for (const k of numericKeys) {
                  let sum = 0;
                  let any = false;
                  for (const r of rows) {
                    const v = r[k];
                    if (typeof v === "number" && Number.isFinite(v)) {
                      sum += v;
                      any = true;
                    }
                  }
                  if (any) totals[k] = sum;
                }
                if (Object.keys(totals).length < 1) return null;
                return (
                  <tfoot className="sticky bottom-0 bg-slate-100">
                    <tr className="border-t font-semibold text-slate-700" data-testid="drilldown-totals-row">
                      {columns.map((c, idx) => {
                        if (idx === 0) {
                          return (
                            <td key={c.key} className="py-2 px-3 text-[11px] uppercase tracking-wide text-slate-600">
                              Total
                            </td>
                          );
                        }
                        if (totals[c.key] != null) {
                          return (
                            <td key={c.key} className={`py-2 px-3 ${c.align === "right" ? "text-right tabular-nums" : ""}`}>
                              {totals[c.key].toLocaleString("en-IN")}
                            </td>
                          );
                        }
                        return <td key={c.key} className="py-2 px-3 text-slate-300">—</td>;
                      })}
                    </tr>
                  </tfoot>
                );
              })()}
            </table>
          )}
        </div>
        <DialogFooter className="sm:justify-between">
          <div className="text-[12px] text-slate-500">{rows.length} record{rows.length === 1 ? "" : "s"}</div>
          <Button onClick={onDownload} disabled={downloadBusy || rows.length === 0} data-testid="drilldown-download">
            {downloadBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            Download XLSX
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------- Page */
export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // drill-down state
  const [openKey, setOpenKey] = useState(null); // 'parks' | 'pmc' | 'contractor:<id>' | 'attendance' | 'visits:pmc' | 'visits:contractor'
  const [drillRows, setDrillRows] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  // attendance specifics
  const [attDate, setAttDate] = useState(() => istTodayKey());
  const [attFilter, setAttFilter] = useState("all"); // all|fully|partial|missed
  const [attCounts, setAttCounts] = useState({ fully: 0, partial: 0, missed: 0, total: 0 });
  // park-visits-by-role specifics
  const [visitDate, setVisitDate] = useState(() => istTodayKey());
  const [visitFilter, setVisitFilter] = useState("all"); // all|visited|missed
  const [visitCounts, setVisitCounts] = useState({ visited: 0, missed: 0, total: 0 });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/dashboard/admin").then((r) => r.data);
        setData(res);
      } catch (e) {
        // Surface the actual server error (from the try/except wrapper on
        // /api/dashboard/admin) instead of a generic "Failed to load
        // dashboard" — so production data-corruption cases are visible
        // and diagnosable without server-side log access.
        const detail = e?.response?.data?.detail;
        toast.error(
          typeof detail === "string"
            ? detail
            : detail?.message || (e?.response?.status
                ? `Failed to load dashboard (HTTP ${e.response.status})`
                : "Failed to load dashboard"),
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ----------- drill-down loaders ----------- */
  const openParks = async () => {
    setOpenKey("parks");
    setDrillLoading(true);
    try {
      const res = await api.get("/admin/dashboard/parks-list").then((r) => r.data);
      setDrillRows(res.rows);
    } catch {
      toast.error("Failed to load parks");
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  };

  const openSupervisors = async (role, contractorId, label) => {
    setOpenKey(role === "pmc_supervisor" ? "pmc" : `contractor:${contractorId || ""}|${label}`);
    setDrillLoading(true);
    try {
      const q = new URLSearchParams({ role });
      if (contractorId) q.append("contractor_id", contractorId);
      const res = await api.get(`/admin/dashboard/supervisors-list?${q.toString()}`).then((r) => r.data);
      setDrillRows(res.rows);
    } catch {
      toast.error("Failed to load supervisors");
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  };

  const openAttendance = async (filter = "all", date = attDate) => {
    setOpenKey("attendance");
    setAttDate(date);
    setAttFilter(filter);
    setDrillLoading(true);
    try {
      const q = new URLSearchParams({ date });
      if (filter !== "all") q.append("status", filter);
      const res = await api.get(`/admin/dashboard/parks-attendance?${q.toString()}`).then((r) => r.data);
      setDrillRows(res.rows);
      setAttCounts(res.counts || { fully: 0, partial: 0, missed: 0, total: 0 });
    } catch {
      toast.error("Failed to load park attendance");
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  };

  const openVisits = async (role, filter = "all", date = visitDate) => {
    setOpenKey(role === "pmc_supervisor" ? "visits:pmc" : "visits:contractor");
    setVisitDate(date);
    setVisitFilter(filter);
    setDrillLoading(true);
    try {
      const q = new URLSearchParams({ role, date });
      const res = await api.get(`/admin/dashboard/park-visits?${q.toString()}`).then((r) => r.data);
      const all = res.rows || [];
      const filtered = filter === "visited" ? all.filter((r) => r.visited)
                     : filter === "missed" ? all.filter((r) => !r.visited)
                     : all;
      setDrillRows(filtered);
      setVisitCounts(res.counts || { visited: 0, missed: 0, total: 0 });
    } catch {
      toast.error("Failed to load park visits");
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  };

  /* ----------- download handlers ----------- */
  const download = async (url, filename) => {
    setDownloadBusy(true);
    try {
      const res = await fetch(`${API_BASE}${url}`, {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloadBusy(false);
    }
  };

  const downloadCurrent = () => {
    if (openKey === "parks") {
      download("/admin/dashboard/parks-list?download=1", "parks.xlsx");
    } else if (openKey === "pmc") {
      download("/admin/dashboard/supervisors-list?role=pmc_supervisor&download=1", "itcot_supervisors.xlsx");
    } else if (openKey?.startsWith("contractor:")) {
      const [cid, label] = openKey.replace("contractor:", "").split("|");
      const q = new URLSearchParams({ role: "contractor_supervisor", download: "1" });
      if (cid) q.append("contractor_id", cid);
      const safe = (label || "contractor").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      download(`/admin/dashboard/supervisors-list?${q.toString()}`, `${safe}_supervisors.xlsx`);
    } else if (openKey === "attendance") {
      const q = new URLSearchParams({ date: attDate, download: "1" });
      if (attFilter !== "all") q.append("status", attFilter);
      download(`/admin/dashboard/parks-attendance?${q.toString()}`, `parks_attendance_${attDate}.xlsx`);
    } else if (openKey === "visits:pmc" || openKey === "visits:contractor") {
      const role = openKey === "visits:pmc" ? "pmc_supervisor" : "contractor_supervisor";
      const label = openKey === "visits:pmc" ? "itcot" : "contractor";
      const q = new URLSearchParams({ role, date: visitDate, download: "1" });
      download(`/admin/dashboard/park-visits?${q.toString()}`, `park_visits_${label}_${visitDate}.xlsx`);
    }
  };

  /* ----------- columns ----------- */
  const parksCols = useMemo(() => [
    { key: "sno", label: "S.No", render: (_r, i) => i + 1, align: "right" },
    { key: "code", label: "Park Code" },
    { key: "name", label: "Park Name" },
    { key: "district", label: "District" },
    {
      key: "active", label: "Status",
      render: (r) => (
        <Badge variant="outline" className={r.active ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-600"}>
          {r.active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    { key: "approved_sweeper", label: "Sanc. Sweeper", align: "right" },
    { key: "approved_driver", label: "Sanc. Driver", align: "right" },
    { key: "approved_supervisor", label: "Sanc. Sup.", align: "right" },
    { key: "active_employees", label: "Active Emp.", align: "right" },
  ], []);

  const supervisorCols = useMemo(() => [
    { key: "sno", label: "S.No", render: (_r, i) => i + 1, align: "right" },
    { key: "username", label: "Login ID" },
    { key: "full_name", label: "Full Name" },
    { key: "phone", label: "Phone", render: (r) => r.phone || "—" },
    { key: "assigned_parks_count", label: "Parks", align: "right" },
    {
      key: "assigned_parks_detail",
      label: (
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 whitespace-nowrap">
          <span>Assigned Parks</span>
          <span className="inline-flex items-center gap-2 text-[10px] font-normal normal-case text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />Visited</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-amber-500" />Not visited</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-slate-400" />Pending</span>
          </span>
        </span>
      ),
      render: (r) => {
        const list = r.assigned_parks_detail || [];
        if (list.length === 0) return <span className="text-slate-400">—</span>;
        const visible = list.slice(0, 6);
        const stateClass = (s) => {
          if (s === "visited") return "border-emerald-300 bg-emerald-50 text-emerald-800";
          if (s === "not_visited") return "border-amber-300 bg-amber-50 text-amber-800";
          return "border-slate-200 bg-slate-50 text-slate-500";
        };
        const stateLabel = (s) =>
          s === "visited" ? "Visited today"
            : s === "not_visited" ? "Marked Not Visited today"
            : "Not yet visited / not marked";
        return (
          <div className="flex flex-wrap gap-1" data-testid={`sup-${r.username}-parks`}>
            {visible.map((p) => {
              const st = p.visit_state || (p.visited ? "visited" : "pending");
              return (
                <Badge
                  key={p.id}
                  variant="outline"
                  className={`${stateClass(st)} text-[10px]`}
                  title={`${p.code} ${p.name} — ${stateLabel(st)}`}
                  data-testid={`sup-${r.username}-park-${p.code}-${st}`}
                >
                  {p.code ? <span className="mr-1 font-mono opacity-70">{p.code}</span> : null}
                  {p.name}
                </Badge>
              );
            })}
            {list.length > visible.length && <Badge variant="outline" className="text-[10px]">+{list.length - visible.length}</Badge>}
          </div>
        );
      },
    },
    {
      key: "visited_today_count", label: "Visited Today",
      align: "right",
      render: (r) => {
        const v = r.visited_today_count || 0;
        const total = r.assigned_parks_count || 0;
        return (
          <span
            className={
              "tabular-nums text-[12px] " +
              (total === 0
                ? "text-slate-400"
                : v === total
                  ? "text-emerald-700 font-medium"
                  : v > 0
                    ? "text-amber-700"
                    : "text-rose-700")
            }
            data-testid={`sup-${r.username}-visited-count`}
          >
            {v}/{total}
          </span>
        );
      },
    },
  ], []);

  const visitsCols = useMemo(() => {
    const Dot = ({ ok, title }) => (
      <span
        className="inline-block h-3 w-3 rounded-full ring-2 ring-white"
        style={{ backgroundColor: ok ? "#10b981" : "#ef4444" }}
        title={title}
      />
    );
    const StatusHeader = ({ children }) => (
      <span className="inline-flex flex-col items-center text-center leading-tight">
        <span>{children}</span>
        <span className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-normal normal-case text-slate-500">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />Yes
          <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-rose-500" />No
        </span>
      </span>
    );
    const isPmc = openKey === "visits:pmc";
    const cols = [
      { key: "sno", label: "S.No", render: (_r, i) => i + 1, align: "right" },
      { key: "park_code", label: "Park Code" },
      { key: "park_name", label: "Park Name" },
      {
        key: "attendance_done",
        label: <StatusHeader>Attendance</StatusHeader>,
        render: (r) => (
          <div className="flex justify-center">
            <Dot ok={!!r.attendance_done} title={r.attendance_done ? "Attendance marked" : "Attendance NOT marked"} />
          </div>
        ),
      },
    ];
    // Cleaning checklists are PMC-only responsibilities. Contractors only
    // capture attendance, so we hide the Before/After clean columns for the
    // contractor visits drilldown.
    if (isPmc) {
      cols.push(
        {
          key: "before_done",
          label: <StatusHeader>Before clean</StatusHeader>,
          render: (r) => (
            <div className="flex justify-center">
              <Dot ok={!!r.before_done} title={r.before_done ? "Before-cleaning completed" : "Before-cleaning pending"} />
            </div>
          ),
        },
        {
          key: "after_done",
          label: <StatusHeader>After clean</StatusHeader>,
          render: (r) => (
            <div className="flex justify-center">
              <Dot ok={!!r.after_done} title={r.after_done ? "After-cleaning completed" : "After-cleaning pending"} />
            </div>
          ),
        },
      );
    }
    cols.push({
      key: "assigned", label: "Assigned Supervisor",
      render: (r) => {
        const v = r.assigned || [];
        if (!v.length) return <span className="text-slate-400">— No supervisor assigned</span>;
        return (
          <div className="flex flex-col gap-0.5">
            {v.map((u, i) => (
              <span key={i} className="text-[12px]">
                <span className="font-medium">{u.name}</span>
                <span className="ml-1.5 text-[10px] text-slate-400">({u.username})</span>
              </span>
            ))}
          </div>
        );
      },
    });
    return cols;
  }, [openKey]);

  const attendanceCols = useMemo(() => {
    const ChipGroup = ({ chips, visited, roleLabel }) => {
      if (!visited) {
        return (
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700" title={`${roleLabel} did not visit today`}>
            Not visited
          </Badge>
        );
      }
      if (!chips || chips.length === 0) {
        return <span className="text-slate-400 text-[12px]">—</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {chips.map((c) => (
            <Badge
              key={c.id}
              variant="outline"
              className={
                c.state === "captured"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-rose-300 bg-rose-50 text-rose-700"
              }
              title={`${c.label} — ${c.state === "captured" ? "captured" : "missed"}`}
            >
              {c.label}
            </Badge>
          ))}
        </div>
      );
    };
    return [
      { key: "sno", label: "S.No", render: (_r, i) => i + 1, align: "right" },
      { key: "park_code", label: "Park Code" },
      { key: "park_name", label: "Park Name" },
      {
        key: "status", label: "Overall",
        render: (r) => {
          const map = {
            fully: { c: "border-emerald-300 bg-emerald-50 text-emerald-700", t: "Fully marked" },
            partial: { c: "border-amber-300 bg-amber-50 text-amber-700", t: "Partial" },
            missed: { c: "border-rose-300 bg-rose-50 text-rose-700", t: "Not marked" },
          };
          const s = map[r.status] || map.missed;
          return <Badge variant="outline" className={s.c}>{s.t}</Badge>;
        },
      },
      {
        key: "pmc_windows", label: "ITCOT Windows",
        render: (r) => <ChipGroup chips={r.pmc_chips} visited={r.pmc_visited} roleLabel="ITCOT" />,
      },
      {
        key: "con_windows", label: "Contractor Windows",
        render: (r) => <ChipGroup chips={r.contractor_chips} visited={r.contractor_visited} roleLabel="Contractor" />,
      },
      {
        key: "adherence", label: "Captured", align: "right",
        render: (r) => `${r.captured_count || 0}/${r.configured_count || 0}`,
      },
    ];
  }, []);

  const drillCols = openKey === "parks" ? parksCols
    : openKey === "attendance" ? attendanceCols
    : (openKey === "visits:pmc" || openKey === "visits:contractor") ? visitsCols
    : supervisorCols;

  const drillTitle = openKey === "parks" ? "All Parks"
    : openKey === "pmc" ? "ITCOT Supervisors"
    : openKey?.startsWith("contractor:") ? `${openKey.split("|")[1] || "Contractor"} — Supervisors`
    : openKey === "attendance" ? `Park Attendance — ${fmtDdMmYyyy(attDate)}`
    : openKey === "visits:pmc" ? `ITCOT Park Visits — ${fmtDdMmYyyy(visitDate)}`
    : openKey === "visits:contractor" ? `Contractor Park Visits — ${fmtDdMmYyyy(visitDate)}`
    : "";

  const visitsExtraHeader = (openKey === "visits:pmc" || openKey === "visits:contractor") ? (
    <div className="flex flex-wrap items-center gap-3 mt-2">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-slate-500" />
        <Input
          type="date"
          value={visitDate}
          onChange={(e) => openVisits(openKey === "visits:pmc" ? "pmc_supervisor" : "contractor_supervisor", visitFilter, e.target.value)}
          className="h-8 w-[160px]"
          data-testid="drilldown-visit-date"
        />
      </div>
      <Select value={visitFilter} onValueChange={(v) => openVisits(openKey === "visits:pmc" ? "pmc_supervisor" : "contractor_supervisor", v, visitDate)}>
        <SelectTrigger className="h-8 w-[180px]" data-testid="drilldown-visit-filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All ({visitCounts.total})</SelectItem>
          <SelectItem value="visited">Visited ({visitCounts.visited})</SelectItem>
          <SelectItem value="missed">Not visited ({visitCounts.missed})</SelectItem>
        </SelectContent>
      </Select>
      <div className="ml-auto flex items-center gap-2 text-[12px]">
        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">
          <CheckCircle2 className="mr-1 h-3 w-3" />Visited {visitCounts.visited}
        </Badge>
        <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">
          <XCircle className="mr-1 h-3 w-3" />Not visited {visitCounts.missed}
        </Badge>
      </div>
    </div>
  ) : null;

  const attendanceExtraHeader = openKey === "attendance" ? (
    <div className="flex flex-wrap items-center gap-3 mt-2">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-slate-500" />
        <Input
          type="date"
          value={attDate}
          onChange={(e) => openAttendance(attFilter, e.target.value)}
          className="h-8 w-[160px]"
          data-testid="drilldown-att-date"
        />
      </div>
      <Select value={attFilter} onValueChange={(v) => openAttendance(v, attDate)}>
        <SelectTrigger className="h-8 w-[180px]" data-testid="drilldown-att-filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All ({attCounts.total})</SelectItem>
          <SelectItem value="fully">Fully marked ({attCounts.fully})</SelectItem>
          <SelectItem value="partial">Partial / pending ({attCounts.partial})</SelectItem>
          <SelectItem value="missed">Not marked ({attCounts.missed})</SelectItem>
        </SelectContent>
      </Select>
      <div className="ml-auto flex items-center gap-2 text-[12px]">
        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">
          <CheckCircle2 className="mr-1 h-3 w-3" />Fully {attCounts.fully}
        </Badge>
        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
          <AlertTriangle className="mr-1 h-3 w-3" />Partial {attCounts.partial}
        </Badge>
        <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">
          <XCircle className="mr-1 h-3 w-3" />Missed {attCounts.missed}
        </Badge>
      </div>
    </div>
  ) : null;

  /* --------------- render --------------- */
  if (loading) {
    return (
      <AppShell title="Operations dashboard" subtitle="Live overview of parks, supervisors and attendance">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="h-[148px] animate-pulse bg-slate-100" />
          ))}
        </div>
      </AppShell>
    );
  }

  const d = data || {};
  const parks = d.parks || { active: 0, inactive: 0 };
  const pmc = d.pmc || { active_today: 0, total: 0 };
  const contractors = d.contractors || [];
  const pmcVisits = d.pmc_park_visits || { visited: 0, total: 0 };
  const conVisits = d.contractor_park_visits || { visited: 0, total: 0 };
  const pa = d.parks_attendance || { fully: 0, partial: 0, missed: 0, total: 0 };

  return (
    <AppShell title="Operations dashboard" subtitle={`As of ${fmtDdMmYyyy(istTodayKey())} (IST)`}>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {/* 1. Total Parks */}
        <Tile
          accent="indigo"
          icon={MapPin}
          title="Total Parks"
          subtitle={parks.inactive > 0 ? `(${parks.inactive} Inactive)` : null}
          value={parks.active}
          onClick={openParks}
          testid="tile-parks"
        />

        {/* 2. ITCOT Supervisors — active today / total */}
        <Tile
          accent="emerald"
          icon={Users2}
          title={pmc.label || "ITCOT Supervisors"}
          subtitle={`${pmc.active_today} active today / ${pmc.total} total`}
          value={`${pmc.active_today}/${pmc.total}`}
          onClick={() => openSupervisors("pmc_supervisor", null, "ITCOT")}
          testid="tile-pmc-supervisors"
        />

        {/* 3. Contractor cards — one per contractor company */}
        {contractors.map((c) => (
          <Tile
            key={c.id}
            accent="amber"
            icon={HardHat}
            title={c.name}
            subtitle={`${c.active_today} active today / ${c.total} total`}
            value={`${c.active_today}/${c.total}`}
            onClick={() => openSupervisors("contractor_supervisor", c.id, c.name)}
            testid={`tile-contractor-${c.id}`}
          />
        ))}

        {/* 4. ITCOT Park Visits Today */}
        <Tile
          accent="emerald"
          icon={Footprints}
          title="ITCOT Park Visits · Today"
          subtitle={`${pmcVisits.visited} visited / ${pmcVisits.total} parks`}
          value={`${pmcVisits.visited}/${pmcVisits.total}`}
          onClick={() => openVisits("pmc_supervisor", "all", istTodayKey())}
          testid="tile-itcot-visits"
        />

        {/* 5. Contractor Park Visits Today */}
        <Tile
          accent="amber"
          icon={Footprints}
          title="Contractor Park Visits · Today"
          subtitle={`${conVisits.visited} visited / ${conVisits.total} parks`}
          value={`${conVisits.visited}/${conVisits.total}`}
          onClick={() => openVisits("contractor_supervisor", "all", istTodayKey())}
          testid="tile-contractor-visits"
        />

        {/* 6. Window Adherence Today */}
        <Tile
          accent="sky"
          icon={ClipboardCheck}
          title="Window Adherence · Today"
          subtitle={`${pa.fully} fully · ${pa.partial} partial · ${pa.missed} missed`}
          value={`${pa.fully}/${pa.total}`}
          onClick={() => openAttendance("all", istTodayKey())}
          testid="tile-park-attendance"
        >
          <div className="mt-3 flex gap-1.5">
            <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px]">
              ✓ {pa.fully}
            </Badge>
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]">
              ! {pa.partial}
            </Badge>
            <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700 text-[10px]">
              ✗ {pa.missed}
            </Badge>
          </div>
        </Tile>
      </div>

      <DrillDownDialog
        open={!!openKey}
        onOpenChange={(o) => { if (!o) setOpenKey(null); }}
        title={drillTitle}
        subtitle={
          openKey === "attendance" ? "Per-park adherence to configured attendance windows"
          : (openKey === "visits:pmc" || openKey === "visits:contractor") ? "Per-park visit status by supervisor"
          : null
        }
        columns={drillCols}
        rows={drillRows}
        loading={drillLoading}
        onDownload={downloadCurrent}
        downloadBusy={downloadBusy}
        extraHeader={attendanceExtraHeader || visitsExtraHeader}
      />
    </AppShell>
  );
}
